// This file is auto-generated by @hey-api/openapi-ts

import {
  BICFromCoreSchema,
  IBANFromCoreSchema,
  PhoneNumberFromCoreSchema,
} from '../validations/customFormatSchemas'
import { z } from 'zod'

/**
 * Tilinumero IBAN-muodossa. Voi olla tyhjä merkkijono, jos tilinumeroa ei ole tiedossa.
 */
export const IbanSchema = IBANFromCoreSchema

export type Iban = z.infer<typeof IbanSchema>

/**
 * Pankin BIC-tunnus. Voi olla tyhjä merkkijono, jos tunnusta ei ole tiedossa.
 */
export const BicSchema = BICFromCoreSchema

export type Bic = z.infer<typeof BicSchema>

/**
 * Puhelinnumero. Valinnainen +-etuliite ja sen jälkeen numerot ilman välilyöntejä.
 */
export const PhoneNumberSchema = PhoneNumberFromCoreSchema

export type PhoneNumber = z.infer<typeof PhoneNumberSchema>

/**
 * resident/error-schema
 */
export const ResidentAppErrorSchema = z.object({
  message: z.string().optional(),
  statusCode: z.number().int(),
  residentApiErrorType: z
    .enum([
      'NoActiveContracts',
      'ErrorFetchingContracts',
      'ErrorCreatingDefectNotice',
      'ErrorGettingDefectNoticeLocations',
      'ErrorUnauthorizedFeatureAccess',
      'ErrorUnauthorizedAuthLevel',
      'ErrorTerminationMovingOutDateInvalid',
      'ErrorTerminationMissingResidentCredentials',
      'ErrorTerminationPendingRequest',
      'ErrorTimeout',
    ])
    .optional(),
})

export type ResidentAppError = z.infer<typeof ResidentAppErrorSchema>

/**
 * resident/user/get
 *
 * Käyttäjän tiedot
 */
export const UserDetailsSchema = z.object({
  uuid: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().email().nullable(),
  phoneNumber: z.string().nullable(),
  cognitoId: z.string().nullable(),
  eServicesConsent: z.boolean(),
})

export type UserDetails = z.infer<typeof UserDetailsSchema>

/**
 * resident/user/notifications/get
 *
 * Käyttäjän ilmoitukset
 */
export const UserNotificationSchema = z.object({
  contractUuid: z.string().uuid(),
  id: z.number().int(),
  priority: z.enum(['important', 'normal']).default('normal'),
  category: z.enum([
    'arrear',
    'contract_lifecycle',
    'contract_modification_request_approval_needed',
    'contract_modification_request_created',
    'contract_modification_request_kenno_accepted',
    'contract_modification_request_kenno_rejected',
    'contract_modification_request_tenant_accepted',
    'contract_modification_request_tenant_rejected',
    'contract_termination_request_approval_needed',
    'contract_termination_request_created',
    'contract_termination_request_kenno_accepted',
    'contract_termination_request_kenno_rejected',
    'contract_termination_request_tenant_accepted',
    'contract_termination_request_tenant_rejected',
    'normal',
    'message',
  ]),
  notificationMeta: z
    .object({
      arrearAmount: z.string(),
    })
    .nullable(),
  isCloseable: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  closedAt: z.string().datetime({ offset: true }).optional(),
})

export type UserNotification = z.infer<typeof UserNotificationSchema>

/**
 * resident/userPermissions/get
 *
 * Kirjautuneen käyttäjän oikeudet asukassivuilla
 */
export const UserPermissionsSchema = z.object({
  authLevel: z.enum(['strong', 'weak']).optional(),
  contracts: z
    .array(
      z.object({
        contractUuid: z.string().uuid().optional(),
        tenantRoles: z.array(z.string()).optional(),
      })
    )
    .optional(),
})

export type UserPermissions = z.infer<typeof UserPermissionsSchema>

/**
 * resident/contract/terminationDisallowedReason
 *
 * Sopimuksen irtisanomista estävä syy
 */
export const TerminationDisallowedReasonEnumSchema = z.enum([
  'contract_terminated',
  'pending_termination_tenant_approvals',
  'pending_termination_review',
  'is_estate_contract',
  'has_guardian_of_interests',
  'missing_resident_users',
  'contract_tag',
  'not_contract_party',
  'tenants_with_undetermined_age',
  'has_company_tenant',
  'pending_modification_request',
  'not_started_yet',
  'open_continuation_offer',
  'pending_contract_resign',
])

export type TerminationDisallowedReasonEnum = z.infer<
  typeof TerminationDisallowedReasonEnumSchema
>

/**
 * resident/contract/status
 *
 * Sopimuksen tila: allekirjoitettu, voimassa, irtisanottu
 */
export const ContractStatusEnumSchema = z.enum([
  'signed',
  'active',
  'terminated',
])

export type ContractStatusEnum = z.infer<typeof ContractStatusEnumSchema>

/**
 * resident/contract/contractType
 *
 * Sopimuksen tyyppi: vuokra, asumisoikeus, liiketila, pysäköintipaikka
 */
export const ContractTypeEnumSchema = z.enum([
  'rental',
  'right_of_occupancy',
  'retail',
  'parking_spot',
])

export type ContractTypeEnum = z.infer<typeof ContractTypeEnumSchema>

/**
 * resident/contract/postaladdress
 *
 * Postiosoite
 */
export const PostalAddressSchema = z.object({
  streetAddressLine1: z.string(),
  streetAddressLine2: z.string().nullable(),
  city: z.string(),
  postalCode: z.string(),
  countrySubdivision: z.string().nullish(),
})

export type PostalAddress = z.infer<typeof PostalAddressSchema>

/**
 * resident/contract/addressDetails
 *
 * Erikseen eritellyt osoitetiedot
 */
export const AddressDetailsSchema = z.object({
  staircaseLetter: z.string().nullable(),
  apartmentNumber: z.string().nullable(),
})

export type AddressDetails = z.infer<typeof AddressDetailsSchema>

/**
 * resident/contract/collateral
 *
 * Panttaustiedot
 */
export const CollateralSchema = z.object({
  bankName: z.string().nullable(),
  isCollateral: z.boolean(),
})

export type Collateral = z.infer<typeof CollateralSchema>

export const ContractModificationResponseBodySchema = z.object({
  uuid: z.string().uuid(),
  contractId: z.number().optional(),
  createdAt: z.string().datetime({ offset: true }).optional(),
  createdByResidentUserId: z.number().optional(),
  id: z.number().optional(),
  status: z
    .enum([
      'approved',
      'pending_tenant_approval',
      'ready_for_review',
      'rejected',
      'pending_strong_authentication',
    ])
    .optional(),
  updatedAt: z.string().datetime({ offset: true }).optional(),
})

export type ContractModificationResponseBody = z.infer<
  typeof ContractModificationResponseBodySchema
>

export const ContractModificationResponseWithEventsBodySchema = z.object({
  uuid: z.string().uuid(),
  contractId: z.number().optional(),
  createdAt: z.string().datetime({ offset: true }).optional(),
  createdByResidentUserId: z.number().optional(),
  createdBy: z.object({
    tenantUuid: z.string().uuid(),
    residentUserUuid: z.string().uuid(),
  }),
  id: z.number().optional(),
  status: z
    .enum([
      'approved',
      'pending_tenant_approval',
      'ready_for_review',
      'rejected',
      'pending_strong_authentication',
    ])
    .optional(),
  updatedAt: z.string().datetime({ offset: true }).optional(),
  transferTenantEvent: z
    .object({
      contractModificationEventId: z.number().optional(),
      destinationTenantId: z.number(),
      sourceTenantId: z.number(),
    })
    .optional(),
  removeTenantEvents: z
    .array(
      z.object({
        contractModificationEventId: z.number().optional(),
        removeTenantId: z.number(),
      })
    )
    .optional(),
  newTenantEvents: z
    .array(
      z.object({
        birthDate: z.string().optional(),
        contractModificationEventId: z.number().optional(),
        email: z.string().optional(),
        firstName: z.string().optional(),
        id: z.number().optional(),
        lastName: z.string().optional(),
        phoneNumber: z.string().optional(),
        socialSecurityNumber: z.string().optional(),
      })
    )
    .optional(),
})

export type ContractModificationResponseWithEventsBody = z.infer<
  typeof ContractModificationResponseWithEventsBodySchema
>

/**
 * Bulletin distribution. Distribution can target properties, property companies, or both.
 */
export const BulletinAudienceSchema = z.object({
  costPoolId: z.number().int().nullable(),
  costPoolIdentifier: z.string().nullable(),
  costPoolDisplayName: z.string().nullable(),
  propertyCompanyIds: z.array(z.number().int()),
})

export type BulletinAudience = z.infer<typeof BulletinAudienceSchema>

/**
 * resident/contractBulletin/SingleContractBulletinItem
 *
 * Yksittäisen ilmoituksen tiedot
 */
export const BulletinResponseBodySchema = z.object({
  uuid: z.string().uuid(),
  title: z.string(),
  excerpt: z.string(),
  publishedAt: z.string(),
  updatedAt: z.string().nullable(),
  author: z.string().nullish(),
  featuredImage: z
    .object({
      url: z.string(),
      alt: z.string(),
    })
    .nullable(),
  body: z.string(),
  attachments: z.array(
    z.object({
      uuid: z.string().uuid(),
      name: z.string(),
    })
  ),
  audience: BulletinAudienceSchema.optional(),
  language: z.enum(['fi', 'sv', 'en']),
  availableLanguages: z.array(z.enum(['fi', 'sv', 'en'])),
})

export type BulletinResponseBody = z.infer<typeof BulletinResponseBodySchema>

/**
 * resident/operation-confirmation/confirmationResponse
 *
 * Operaation alustuksen vastaus
 */
export const ConfirmOperationInitResponseBodySchema = z.object({
  nonce: z.string(),
})

export type ConfirmOperationInitResponseBody = z.infer<
  typeof ConfirmOperationInitResponseBodySchema
>

/**
 * resident/operation-confirmation/contractTerminationOperationExecutionResponse
 *
 * Operaation suorittamisen vastaus
 */
export const ConfirmOperationExecuteResponseBodySchema = z.object({
  data: z
    .object({
      success: z.boolean(),
      errorState: z.string().optional(),
    })
    .optional(),
})

export type ConfirmOperationExecuteResponseBody = z.infer<
  typeof ConfirmOperationExecuteResponseBodySchema
>

/**
 * resident/contractBeneficiary/get
 *
 * Maksunsaaja
 */
export const BeneficiarySchema = z.object({
  id: z.number().int(),
  tenantId: z.number().int().nullable(),
  beneficiaryName: z.string(),
  iban: IbanSchema,
  bic: BicSchema,
  returnPercentage: z.number().int().gte(0).lte(100).nullable(),
  rooReturnPercentage: z.number().int().gte(0).lte(100).nullable(),
})

export type Beneficiary = z.infer<typeof BeneficiarySchema>

/**
 * resident/file/complete/postRequest
 *
 * Tiedoston lähetyksen viimeistely
 */
export const FileCompleteRequestBodySchema = z.object({
  file: z.object({
    uuid: z.string().uuid(),
  }),
})

export type FileCompleteRequestBody = z.infer<
  typeof FileCompleteRequestBodySchema
>

/**
 * resident/contract/paymentsByMonth/get
 *
 * Laskuerittelyt kuukausittain
 */
export const PaymentsByMonthSchema = z.object({
  yearMonth: z.string(),
  difference: z.string(),
  monthlyDifference: z.string(),
  paymentsByTarget: z.array(
    z.object({
      targetId: z.number().int(),
      invoiceUuid: z.string().uuid().nullable(),
      nameOnBill: z.string(),
      dueDate: z.string().date(),
      expected: z.string(),
      originalValue: z.string(),
      appliedCreditNotes: z.array(
        z.object({
          amount: z.string(),
          message: z.string().nullable(),
          date: z.string().date(),
        })
      ),
    })
  ),
  withholdingsUsed: z.array(
    z.object({
      date: z.string().date(),
      amount: z.string(),
      withheldFrom: z.enum(['securityDeposit', 'depositVoucher', 'rooFee']),
    })
  ),
  paymentOrders: z.array(
    z.object({
      amount: z.string(),
      executionDate: z.string().date(),
    })
  ),
  openCreditNotes: z.array(
    z.object({
      invoiceUuid: z.string().uuid().nullable(),
      nameOnBill: z.string(),
      message: z.string().nullable(),
      amount: z.string(),
      date: z.string().date(),
    })
  ),
})

export type PaymentsByMonth = z.infer<typeof PaymentsByMonthSchema>

/**
 * resident/contract/remittance/get
 *
 * Maksutapahtuma
 */
export const RemittanceSchema = z.object({
  amount: z.string(),
  paidDate: z.string().date(),
  nameOfPayer: z.string().nullable(),
})

export type Remittance = z.infer<typeof RemittanceSchema>

/**
 * resident/contract/payment-category/get
 */
export const PaymentCategorySchema = z.object({
  category: z.enum([
    'charges_for_utilities',
    'generic',
    'interest',
    'rent',
    'residence_charge',
    'right_of_occupancy_deposit',
    'right_of_occupancy_new_development_deposit',
    'security_deposit',
  ]),
  bankAccounts: z.array(
    z.object({
      iban: IbanSchema,
      bic: BicSchema,
      bankName: z.string(),
    })
  ),
})

export type PaymentCategory = z.infer<typeof PaymentCategorySchema>

/**
 * resident/defect-notice/classes
 *
 * Vikaluokat
 */
export const DefectNoticeClassesSchema = z.array(
  z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
    z.literal(10),
    z.literal(11),
    z.literal(12),
    z.literal(13),
    z.literal(14),
    z.literal(15),
    z.literal(16),
    z.literal(17),
    z.literal(18),
    z.literal(19),
    z.literal(20),
    z.literal(21),
  ])
)

export type DefectNoticeClasses = z.infer<typeof DefectNoticeClassesSchema>

/**
 * resident/defect-notice/response
 */
export const CreateDefectNoticeResponseBodySchema = z.object({
  id: z.number().int(),
})

export type CreateDefectNoticeResponseBody = z.infer<
  typeof CreateDefectNoticeResponseBodySchema
>

/**
 * resident/question
 *
 * Kysymyksen luonti
 */
export const CreateQuestionRequestBodySchema = z.object({
  title: z.string().nullish(),
  description: z.string().nullish(),
  fileUuids: z.array(z.string().uuid()),
  contractUuid: z.string().uuid(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string(),
    })
    .nullish(),
})

export type CreateQuestionRequestBody = z.infer<
  typeof CreateQuestionRequestBodySchema
>

/**
 * resident/question/response
 */
export const CreateQuestionResponseBodySchema = z.object({
  id: z.number().int(),
})

export type CreateQuestionResponseBody = z.infer<
  typeof CreateQuestionResponseBodySchema
>

/**
 * resident/feedback
 *
 * Palautteen luonti
 */
export const CreateFeedbackRequestBodySchema = z.object({
  title: z.string().nullish(),
  description: z.string().nullish(),
  fileUuids: z.array(z.string().uuid()),
  contractUuid: z.string().uuid(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string(),
    })
    .nullish(),
})

export type CreateFeedbackRequestBody = z.infer<
  typeof CreateFeedbackRequestBodySchema
>

/**
 * resident/feedback/response
 */
export const CreateFeedbackResponseBodySchema = z.object({
  id: z.number().int(),
})

export type CreateFeedbackResponseBody = z.infer<
  typeof CreateFeedbackResponseBodySchema
>

/**
 * resident/realProperty/contact/get
 *
 * Sopimuksen kiinteisön eri tahojen yhteystiedot
 */
export const ContractContactSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  phoneNumber: z.string().nullish(),
  email: z.string(),
  serviceRole: z.string(),
  officeName: z.string(),
  contactCategory: z.enum([
    'property_maintenance',
    'customer_service',
    'property_management',
    'sales_representative',
    'resident_committee',
    'other',
  ]),
})

export type ContractContact = z.infer<typeof ContractContactSchema>

/**
 * Sopimuksen velkaerittely
 */
export const ContractDebtSchema = z.object({
  currentDue: z.string(),
  debtByMonth: z.array(
    z.object({
      currentDue: z.string(),
      debtByTarget: z.array(
        z.object({
          bankAccount: z.string(),
          currentDue: z.string(),
          dueDate: z.string().date(),
          nameOnBill: z.string(),
          targetId: z.number().int(),
        })
      ),
      yearMonth: z.string(),
    })
  ),
  referenceNumber: z.string(),
})

export type ContractDebt = z.infer<typeof ContractDebtSchema>

/**
 * resident/document/get
 *
 * Dokumentti
 */
export const DocumentsResponseSchema = z.object({
  id: z.number().int(),
  uuid: z.string().uuid(),
  name: z.string(),
  documentCategory: z
    .object({
      id: z.number().int(),
      labels: z.object({
        fi: z.string(),
        sv: z.string(),
        en: z.string(),
      }),
    })
    .nullable(),
  documentType: z.string(),
})

export type DocumentsResponse = z.infer<typeof DocumentsResponseSchema>

/**
 * resident/contract-document/get
 *
 * Sopimusdokumentin saatavuus
 */
export const ContractDocumentResponseSchema = z.object({
  available: z.boolean(),
  fileName: z.string().nullable(),
})

export type ContractDocumentResponse = z.infer<
  typeof ContractDocumentResponseSchema
>

/**
 * resident/contract-document/content/get
 *
 * Sopimusdokumentin sisältö
 */
export const ContractDocumentContentResponseSchema = z.object({
  fileName: z.string(),
  contentType: z.string(),
  contentBase64: z.string(),
})

export type ContractDocumentContentResponse = z.infer<
  typeof ContractDocumentContentResponseSchema
>

/**
 * Response containing paginated bulletins
 */
export const PaginatedBulletinsResponseSchema = z.object({
  bulletins: z.array(
    z.object({
      uuid: z.string().uuid(),
      title: z.string(),
      publishedAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }).nullish(),
      featuredImage: z
        .object({
          url: z.string().url().optional(),
          alt: z.string().optional(),
        })
        .nullish(),
      excerpt: z.string(),
      author: z.string().nullish(),
      audience: BulletinAudienceSchema,
      language: z.enum(['fi', 'sv', 'en']),
      availableLanguages: z.array(z.enum(['fi', 'sv', 'en'])),
    })
  ),
  nextPage: z.number().int().nullable(),
})

export type PaginatedBulletinsResponse = z.infer<
  typeof PaginatedBulletinsResponseSchema
>

/**
 * Sopimuksen kuukausittaisten maksutavoitteiden summat
 */
export const CurrentPaymentTargetsResponseSchema = z.object({
  monthlyRent: z.number().optional(),
  monthlyWaterAdvance: z.number().optional(),
})

export type CurrentPaymentTargetsResponse = z.infer<
  typeof CurrentPaymentTargetsResponseSchema
>

/**
 * Sopimuksen vakuustietojen paluusanoma
 */
export const ContractSecurityDepositResponseSchema = z.object({
  targetId: z.number().int(),
  amount: z.number(),
  totalAmount: z.number(),
  remainingUnpaid: z.number(),
  dueDate: z.string().date(),
  usedInPaymentOrders: z.number(),
  usedInWithholdings: z.number(),
  totalUsed: z.number(),
  remaining: z.number(),
  voucher: z
    .object({
      id: z.number().int().optional(),
      usedInVoucherWithholdings: z.number().optional(),
      amount: z.number().optional(),
      remaining: z.number().optional(),
      redeemedOrRelinquished: z.boolean().optional(),
    })
    .nullish(),
})

export type ContractSecurityDepositResponse = z.infer<
  typeof ContractSecurityDepositResponseSchema
>

export const ContractModificationEventNewTenantSchema = z.object({
  socialSecurityNumber: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  birthDate: z.string().date().optional(),
})

export type ContractModificationEventNewTenant = z.infer<
  typeof ContractModificationEventNewTenantSchema
>

export const DefectNoticePatchRequestBodySchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  defectClassId: z.number().int().gte(1).optional(),
  hasPetsInApartment: z.boolean().optional(),
  masterKeyAccessAllowed: z.boolean().optional(),
})

export type DefectNoticePatchRequestBody = z.infer<
  typeof DefectNoticePatchRequestBodySchema
>

export const BuildingSchema = z.object({
  floorCount: z.number().nullable(),
  constructionDate: z.string().nullable(),
  realPropertyAreasTotal: z.number(),
  heatingMethod: z.enum([
    'air_to_water_heat_pump',
    'district',
    'district_and_exhaust_air_heat_pump',
    'district_and_solar',
    'electrical',
    'gas',
    'geothermal',
    'geothermal_and_solar',
    'solar',
    'oil',
    'natural_gas',
    'no_heating',
    'not_set',
  ]),
  realPropertyImageUrl: z.string().nullish(),
  internetOperator: z
    .object({
      email: z.string().nullish(),
      phoneNumber: z.string().nullish(),
      officeName: z.string().nullish(),
    })
    .nullish(),
  internetProvider: z
    .object({
      operatorName: z.string().optional(),
      internetConnection: z.string().nullish(),
      links: z
        .array(
          z.object({
            title: z.string(),
            url: z.string(),
          })
        )
        .optional(),
    })
    .nullish(),
  postalAddress: z
    .object({
      streetAddressLine1: z.string().nullish(),
      streetAddressLine2: z.string().nullish(),
      city: z.string().nullish(),
      postalCode: z.string().nullish(),
      countryName: z.string().nullish(),
      countryCode: z.string().nullish(),
    })
    .nullish(),
  address: z
    .object({
      streetName: z.string().optional(),
      streetNumber: z.string().optional(),
      city: z.string().optional(),
      building: z.string().nullish(),
      region: z.string().nullish(),
      district: z.string().nullish(),
      districtSpecifier: z.string().nullish(),
    })
    .nullish(),
})

export type Building = z.infer<typeof BuildingSchema>

export const ApartmentSchema = z.object({
  rentableApartmentId: z.number(),
  rentalApartmentId: z.number().nullish(),
  rightOfOccupancyApartmentId: z.number().nullish(),
  postalAddress: z
    .object({
      id: z.number().int().optional(),
      city: z.string().optional(),
      streetAddressLine1: z.string().optional(),
      streetAddressLine2: z.string().optional(),
      postalCode: z.string().optional(),
      countryName: z.string().optional(),
      countryCode: z.string().optional(),
    })
    .nullish(),
  floor: z.string().nullish(),
  technicalTypeLabel: z.string().nullish(),
  area: z.string().nullish(),
  salesDescription: z.string().nullable(),
  salesRoomsTypeLabel: z.string(),
  rentalApartmentStatus: z
    .enum([
      'active_contract',
      'active_offer',
      'attachments_received',
      'becoming_vacant',
      'continuation_offer_pending',
      'contract_signature_pending',
      'interested_in_viewing',
      'offer_accepted',
      'rejected',
      'reserved',
      'vacant',
    ])
    .nullish(),
  rightOfOccupancyApartmentStatus: z
    .enum([
      'active_contract',
      'becoming_vacant',
      'continuation_offer_pending',
      'contract_created',
      'offer_round_finished',
      'offer_round_open',
      'reserved',
      'vacant',
    ])
    .nullish(),
  contractStatus: z
    .enum([
      'active',
      'cancelled',
      'draft',
      'expired',
      'signature_cancelled',
      'signature_expired',
      'signed',
      'terminated',
      'waiting_for_signature',
    ])
    .optional(),
  visibleOnHomePage: z.boolean(),
  hasFeature1: z.boolean(),
  hasFeature2: z.boolean(),
  hasFeature3: z.boolean(),
  images: z.array(z.string().url()),
  realPropertyHierarchyId: z.number(),
})

export type Apartment = z.infer<typeof ApartmentSchema>

/**
 * Asukkaan sopimusrooli: ei asukas, väistöasunnon asukas, alivuokralainen, asukas
 */
export const TenantRoleEnumSchema = z.enum([
  'non_tenant',
  'replacement_apartment_tenant',
  'secondary_tenant',
  'subtenant',
  'tenant',
  'legal_guardian',
  'user',
])

export type TenantRoleEnum = z.infer<typeof TenantRoleEnumSchema>

/**
 * resident/contract/tenant
 *
 * Sopimuksen asukastiedot
 */
export const ContractToTenantAssociationSchema = z.object({
  isPayer: z.boolean(),
  isContractParty: z.boolean(),
  tenantRole: TenantRoleEnumSchema,
  id: z.number().int(),
})

export type ContractToTenantAssociation = z.infer<
  typeof ContractToTenantAssociationSchema
>

/**
 * resident/tenant/get
 *
 * Sopimusosapuoli
 */
export const ContractTenantSchema = z.union([
  z.object({
    firstName: z.string(),
    lastName: z.string(),
    id: z.number().int(),
    tenantUuid: z.string().uuid(),
    contractUuid: z.string().uuid(),
    tenantRole: TenantRoleEnumSchema.nullable(),
    isContractParty: z.boolean(),
    isPayer: z.boolean(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    nonDisclosure: z.boolean(),
    isAdult: z.boolean(),
    startDate: z.string().date(),
    endDate: z.string().date().nullable(),
    hasResidentUser: z.boolean(),
  }),
  z.object({
    companyName: z.string(),
    id: z.number().int(),
    tenantUuid: z.string().uuid(),
    contractUuid: z.string().uuid(),
    tenantRole: TenantRoleEnumSchema.nullable(),
    isContractParty: z.boolean(),
    isPayer: z.boolean(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    nonDisclosure: z.boolean(),
    startDate: z.string().date(),
    endDate: z.string().date().nullable(),
  }),
])

export type ContractTenant = z.infer<typeof ContractTenantSchema>

/**
 * Irtisanomisen syy
 */
export const TerminationReasonEnumSchema = z.enum([
  'apartment_does_not_meet_needs',
  'apartment_location',
  'apartment_size',
  'apartment_temperature',
  'area_disturbances',
  'area_poor_services',
  'business_closure',
  'business_closure_in_municipality',
  'business_structure_change',
  'buying_property',
  'death',
  'disturbing_neighbours_or_building',
  'eviction',
  'family_size_change',
  'health_reasons',
  'housing_costs',
  'move_due_work',
  'move_to_another_apartment_of_same_owner',
  'move_to_another_municipality',
  'move_to_another_rental',
  'move_to_care_home_or_hospital',
  'move_to_more_affordable_business_premises_in_municipality',
  'move_to_right_of_occupancy',
  'other',
  'poor_apartment_condition',
  'poor_apartment_equipment_or_properties',
  'poor_property_condition',
  'poor_property_maintenance',
  'poor_transportation_options',
  'renovation',
  'right_of_occupancy_ends',
  'space_needs_change',
  'studies_end',
  'unknown',
])

export type TerminationReasonEnum = z.infer<typeof TerminationReasonEnumSchema>

/**
 * Irtisanomisen syy
 */
export const TerminationReasonInputEnumSchema = z.enum([
  'move_to_another_municipality',
  'family_size_change',
  'move_due_work',
  'apartment_size',
  'poor_apartment_condition',
  'poor_apartment_equipment_or_properties',
  'apartment_does_not_meet_needs',
  'apartment_temperature',
  'apartment_location',
  'poor_property_condition',
  'poor_property_maintenance',
  'housing_costs',
  'studies_end',
  'right_of_occupancy_ends',
  'disturbing_neighbours_or_building',
  'area_disturbances',
  'area_poor_services',
  'poor_transportation_options',
  'health_reasons',
  'death',
  'renovation',
  'business_closure',
  'business_closure_in_municipality',
  'business_structure_change',
  'space_needs_change',
  'move_to_more_affordable_business_premises_in_municipality',
  'other',
])

export type TerminationReasonInputEnum = z.infer<
  typeof TerminationReasonInputEnumSchema
>

/**
 * Mihin asukas muuttaa
 */
export const TerminationDestinationEnumSchema = z.enum([
  'buying_property',
  'move_to_another_apartment_of_same_owner',
  'move_to_another_rental',
  'move_to_care_home_or_hospital',
  'move_to_right_of_occupancy',
  'move_to_right_of_occupancy_of_another_operator',
  'move_to_right_of_occupancy_of_same_owner',
  'other',
  'unknown',
])

export type TerminationDestinationEnum = z.infer<
  typeof TerminationDestinationEnumSchema
>

/**
 * Mihin asukas muuttaa
 */
export const TerminationDestinationInputEnumSchema = z.enum([
  'buying_property',
  'move_to_another_apartment_of_same_owner',
  'move_to_another_rental',
  'move_to_right_of_occupancy_of_same_owner',
  'move_to_right_of_occupancy_of_another_operator',
  'move_to_right_of_occupancy',
  'move_to_care_home_or_hospital',
  'other',
])

export type TerminationDestinationInputEnum = z.infer<
  typeof TerminationDestinationInputEnumSchema
>

/**
 * Sopimuksen irtisanomisilmoituksen luonti
 */
export const ContractTerminationRequestBodySchema = z.object({
  movingOutDate: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
    .nullable(),
  givingContactInfoAllowed: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  hasPetsInApartment: z.boolean(),
  terminationReason: TerminationReasonInputEnumSchema,
  movingDestination: TerminationDestinationInputEnumSchema.optional(),
  endDate: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/),
  displaysSelf: z.boolean(),
  displayStartDate: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
    .optional(),
  displayEndDate: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
    .optional(),
  additionalDetails: z.string().max(1000),
  isCollateral: z.boolean().nullable(),
  collateralLender: z.string().nullable(),
  fileUuids: z.array(z.string()),
  beneficiaries: z.array(
    z.object({
      existingBeneficiaryId: z.number().int().nullish(),
      returnPercentage: z.number().int().gte(0).lte(100),
      rooReturnPercentage: z.number().int().gte(0).lte(100).nullable(),
      removeExistingBeneficiary: z.boolean(),
      contractToTenantAssociationId: z.number().int().nullish(),
      beneficiaryName: z.string(),
      iban: IbanSchema,
      bic: BicSchema.nullable(),
      phoneNumber: z.string().nullish(),
      email: z
        .string()
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
        .nullish(),
    })
  ),
  newAddress: z
    .object({
      recipientName: z.string().min(1).max(100),
      streetAddress: z.string().min(1).max(100),
      postalCode: z.string().min(1).max(100),
      city: z.string().min(1).max(100),
    })
    .optional(),
})

export type ContractTerminationRequestBody = z.infer<
  typeof ContractTerminationRequestBodySchema
>

/**
 * Irtisanomisilmoituksen tila usean sopijan prosessissa.
 *
 * **pending_tenant_approval**: Odottaa muiden sopijapuolien hyväksyntää.
 * Toisten sopijapuolten tulee kutsua approve-endpointia.
 *
 * **ready_for_review**: Kaikki sopijapuolet ovat hyväksyneet tai
 * sopimuksella on vain yksi sopijapuoli. Odottaa isännöitsijän käsittelyä.
 *
 * **approved**: Isännöitsijä on hyväksynyt irtisanomisilmoituksen.
 *
 * **rejected**: Joku sopijapuoli tai isännöitsijä on hylännyt ilmoituksen.
 *
 */
export const TerminationRequestStatusEnumSchema = z.enum([
  'pending_tenant_approval',
  'ready_for_review',
  'approved',
  'rejected',
])

export type TerminationRequestStatusEnum = z.infer<
  typeof TerminationRequestStatusEnumSchema
>

/**
 * resident/contract/terminationRequest
 *
 * Mahdollinen avoin irtisanomisilmoitus
 */
export const TerminationRequestSchema = z.object({
  status: TerminationRequestStatusEnumSchema.nullable(),
  createdAt: z.string().nullable(),
})

export type TerminationRequest = z.infer<typeof TerminationRequestSchema>

/**
 * resident/contract/get
 *
 * Sopimus
 */
export const ContractSchema = z.object({
  uuid: z.string().uuid(),
  startDate: z.string().date(),
  endDate: z.string().date().nullable(),
  realPropertyName: z.string(),
  terminationDisallowedReason: TerminationDisallowedReasonEnumSchema.optional(),
  postalAddress: PostalAddressSchema,
  status: ContractStatusEnumSchema,
  terminationRequest: TerminationRequestSchema.optional(),
  addressDetails: AddressDetailsSchema,
  contractType: ContractTypeEnumSchema,
  propertyCompanyName: z.string(),
  referenceNumber: z.string(),
  collateral: CollateralSchema.optional(),
  propertyCompanyAddress: PostalAddressSchema,
  tenant: ContractToTenantAssociationSchema,
  language: z.string(),
  tenantInspection: z
    .object({
      uuid: z.string().uuid(),
      inspectedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
    })
    .nullable(),
})

export type Contract = z.infer<typeof ContractSchema>

/**
 * resident/contract/termination/get
 *
 * Sopimuksen irtisanomisilmoitus.
 *
 * Sisältää kaikki irtisanomisilmoituksen tiedot sekä prosessin tilan.
 * Usean sopijan tapauksessa status-kenttä kertoo, onko ilmoitus vielä
 * odottamassa muiden sopijapuolien hyväksyntää.
 *
 */
export const ContractTerminationSchema = z.object({
  id: z.number().int(),
  status: TerminationRequestStatusEnumSchema,
  endDate: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/),
  movingOutDate: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
    .nullable(),
  displaysSelf: z.boolean(),
  displayStartDate: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/),
  displayEndDate: z.union([
    z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/),
    z.enum(['']),
  ]),
  givingContactInfoAllowed: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  hasPetsInApartment: z.boolean(),
  terminationReason: TerminationReasonEnumSchema,
  movingDestination: TerminationDestinationEnumSchema.optional(),
  additionalDetails: z.string().max(1000),
  isCollateral: z.boolean().nullable(),
  collateralLender: z.string().nullable(),
  beneficiaries: z
    .array(
      z.object({
        beneficiaryName: z.string(),
        iban: IbanSchema,
        bic: BicSchema,
        phoneNumber: z.string().nullable(),
        email: z
          .string()
          .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
          .nullable(),
        returnPercentage: z.number().int().gte(0).lte(100).nullable(),
        rooReturnPercentage: z.number().int().gte(0).lte(100).nullable(),
      })
    )
    .min(0)
    .max(4),
  files: z.array(z.string()),
  newAddress: z
    .object({
      recipientName: z.string().min(1).max(100),
      streetAddress: z.string().min(1).max(100),
      streetAddressLine2: z.string().min(1).max(100).nullish(),
      postalCode: z.string().min(1).max(100),
      city: z.string().min(1).max(100),
      countrySubdivision: z.string().min(1).max(100).nullish(),
      countryCode: z.string().length(2).nullish(),
      countryName: z.string().min(1).max(100).nullish(),
    })
    .optional(),
  createdByResidentUserId: z.number().int(),
  createdBy: z.object({
    tenantUuid: z.string().uuid(),
    residentUserUuid: z.string().uuid(),
  }),
})

export type ContractTermination = z.infer<typeof ContractTerminationSchema>

/**
 * resident/contract/terminationResponse
 */
export const ContractTerminationResponseBodySchema = z.object({
  id: z.number().int(),
  status: TerminationRequestStatusEnumSchema,
})

export type ContractTerminationResponseBody = z.infer<
  typeof ContractTerminationResponseBodySchema
>

/**
 * Irtisanomisilmoituksen hyväksymis-/hylkäysoperaation tyyppi.
 *
 * **resident_app_accept_contract_termination**: Hyväksy toisen sopijapuolen tekemä irtisanomisilmoitus.
 *
 * **resident_app_reject_contract_termination**: Hylkää toisen sopijapuolen tekemä irtisanomisilmoitus.
 *
 */
export const ContractTerminationApprovalOperationTypeEnumSchema = z.enum([
  'resident_app_accept_contract_termination',
  'resident_app_reject_contract_termination',
])

export type ContractTerminationApprovalOperationTypeEnum = z.infer<
  typeof ContractTerminationApprovalOperationTypeEnumSchema
>

/**
 * Sopimusmuutoksen hyväksymis-/hylkäysoperaation tyyppi.
 *
 * **resident_app_accept_contract_modification**: Hyväksy toisen sopijapuolen tekemä sopimusmuutos.
 *
 * **resident_app_reject_contract_modification**: Hylkää toisen sopijapuolen tekemä sopimusmuutos.
 *
 */
export const ContractModificationApprovalOperationTypeEnumSchema = z.enum([
  'resident_app_accept_contract_modification',
  'resident_app_reject_contract_modification',
])

export type ContractModificationApprovalOperationTypeEnum = z.infer<
  typeof ContractModificationApprovalOperationTypeEnumSchema
>

/**
 * Current status of the defect notice
 */
export const DefectNoticeStatusEnumSchema = z.enum([
  'completed',
  'created',
  'in_progress',
])

export type DefectNoticeStatusEnum = z.infer<
  typeof DefectNoticeStatusEnumSchema
>

/**
 * Type of the notice. A notice reported from the resident app can be changed to an observation in Kenno, after which it is read-only for the resident.
 */
export const DefectNoticeTypeEnumSchema = z.enum([
  'defect_notice',
  'observation',
])

export type DefectNoticeTypeEnum = z.infer<typeof DefectNoticeTypeEnumSchema>

export const DefectLocationEnumSchema = z.union([
  z.enum(['CONTRACT_TARGET']),
  z.enum(['COMMON_AREA']),
])

export type DefectLocationEnum = z.infer<typeof DefectLocationEnumSchema>

/**
 * resident/defect-notice
 *
 * Vikailmoituksen luonti
 */
export const CreateDefectNoticeRequestBodySchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  fileUuids: z.array(z.string().uuid()),
  contractUuid: z.string(),
  defectLocation: DefectLocationEnumSchema,
  defectClassId: z.number().int().gte(1).nullable(),
  hasPetsInApartment: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  noticeReporter: z
    .object({
      phoneNumber: z.string(),
      email: z.string(),
    })
    .nullable(),
})

export type CreateDefectNoticeRequestBody = z.infer<
  typeof CreateDefectNoticeRequestBodySchema
>

export const DefectNoticeSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  description: z.string(),
  defectClassId: z.number().int(),
  defectLocation: DefectLocationEnumSchema,
  workOrder: z
    .object({
      id: z.string().optional(),
      title: z.string().optional(),
      description: z.string().optional(),
    })
    .nullish(),
  contractUuid: z.string().uuid(),
  hasPetsInApartment: z.boolean(),
  masterKeyAccessAllowed: z.boolean(),
  noticeReporter: z
    .object({
      phoneNumber: z.string().optional(),
      email: z.string().optional(),
      description: z.string().optional(),
    })
    .nullish(),
  status: DefectNoticeStatusEnumSchema,
  noticeType: DefectNoticeTypeEnumSchema,
  events: z
    .array(
      z.object({
        id: z.number().int(),
        eventType: z.string(),
        createdAt: z.string().datetime({ offset: true }),
        workFinishedAt: z.string().datetime({ offset: true }).nullish(),
      })
    )
    .nullish(),
})

export type DefectNotice = z.infer<typeof DefectNoticeSchema>

export const DefectNoticeListItemSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  description: z.string(),
  defectClassId: z.number().int(),
  defectLocation: DefectLocationEnumSchema,
  workOrder: z
    .object({
      id: z.string().optional(),
      title: z.string().optional(),
      description: z.string().optional(),
    })
    .nullish(),
  status: DefectNoticeStatusEnumSchema,
})

export type DefectNoticeListItem = z.infer<typeof DefectNoticeListItemSchema>

/**
 * Tiedoston content-type
 */
export const FileContentTypeEnumSchema = z.enum([
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

export type FileContentTypeEnum = z.infer<typeof FileContentTypeEnumSchema>

/**
 * resident/file/init/postRequest
 *
 * Tiedoston tallennuksen alustus.
 */
export const FileInitRequestBodySchema = z.object({
  file: z.object({
    fileName: z.string(),
    contentType: FileContentTypeEnumSchema,
  }),
})

export type FileInitRequestBody = z.infer<typeof FileInitRequestBodySchema>

/**
 * Tiedoston tyyppi
 */
export const FileTypeEnumSchema = z.enum(['image', 'document'])

export type FileTypeEnum = z.infer<typeof FileTypeEnumSchema>

/**
 * resident/file/init/postResponse
 *
 * Tiedoston lähetyksen alustuksen paluuarvo
 */
export const FileInitResponseBodySchema = z
  .object({
    id: z.number().int(),
    uuid: z.string().uuid(),
    fileName: z.string(),
    fileType: FileTypeEnumSchema,
    displayFileName: z.string(),
    contentType: FileContentTypeEnumSchema,
    createdByUserId: z.number().int(),
    createdAt: z.string().datetime({ offset: true }),
  })
  .and(
    z.object({
      metadata: z
        .object({
          uploaded: z.boolean(),
          temporary: z.boolean(),
          originalFileName: z.string(),
        })
        .and(
          z.object({
            uploadUrl: z.string(),
            uploadHeaders: z.record(z.string()),
          })
        ),
    })
  )

export type FileInitResponseBody = z.infer<typeof FileInitResponseBodySchema>

/**
 * resident/file/complete/postResponse
 *
 * Tiedoston lähetyksen viimeistelyn paluuarvo
 */
export const FileCompleteResponseBodySchema = z
  .object({
    id: z.number().int(),
    uuid: z.string().uuid(),
    fileName: z.string(),
    fileType: FileTypeEnumSchema,
    displayFileName: z.string(),
    contentType: FileContentTypeEnumSchema,
    createdByUserId: z.number().int(),
    createdAt: z.string().datetime({ offset: true }),
  })
  .and(
    z.object({
      metadata: z.object({
        uploaded: z.boolean(),
        temporary: z.boolean(),
        originalFileName: z.string(),
      }),
    })
  )

export type FileCompleteResponseBody = z.infer<
  typeof FileCompleteResponseBodySchema
>

/**
 * Pysäköintialueen tyyppi
 *
 * - `outdoor`: Ulkoalue.
 * - `building`: Halli.
 */
export const ParkingAreaTypeEnumSchema = z.enum(['outdoor', 'building'])

export type ParkingAreaTypeEnum = z.infer<typeof ParkingAreaTypeEnumSchema>

export const ParkingSpotEquipmentEnumSchema = z.enum([
  'building',
  'covered',
  'disabled_spot',
  'ev_charger',
  'garage',
  'heating_outlet',
  'motorbike_spot',
  'yard',
])

export type ParkingSpotEquipmentEnum = z.infer<
  typeof ParkingSpotEquipmentEnumSchema
>

/**
 * Sähkölaskutustyyppi
 *
 * - `advance_payment`: Ennakko.
 * - `customers_own_contract`: Asukas hoitaa.
 * - `fixed_payment`: Kiinteä maksu.
 * - `included_in_price`: Kuuluu hintaan.
 * - `meter`: Mittari.
 * - `no_electricity`: Ei sähköä.
 */
export const ElectricityBillingTypeEnumSchema = z.enum([
  'advance_payment',
  'customers_own_contract',
  'fixed_payment',
  'included_in_price',
  'meter',
  'no_electricity',
])

export type ElectricityBillingTypeEnum = z.infer<
  typeof ElectricityBillingTypeEnumSchema
>

/**
 * Sopimuksen pysäköintipaikkojen paluusanoma
 */
export const ParkingSpotsResponseSchema = z.object({
  parkingAreas: z.array(
    z.object({
      realPropertyHierarchyId: z.number().int(),
      location: z.object({
        parkingAreaType: ParkingAreaTypeEnumSchema,
        locationDetails: z.string(),
        address: z.object({
          streetName: z.string(),
          streetNumber: z.string(),
          city: z.string(),
        }),
      }),
      parkingSpots: z.array(
        z.object({
          rentableParkingSpotId: z.number().int(),
          isInUse: z.boolean(),
          additionalDetails: z.string(),
          locationDetails: z.string(),
          equipment: z.array(ParkingSpotEquipmentEnumSchema),
          heatingKeyType: z.string().nullable(),
          electricityBillingType: ElectricityBillingTypeEnumSchema,
          prices: z.array(
            z.object({
              price: z.number(),
              validFrom: z.string().date(),
              validTo: z.string().date(),
            })
          ),
          reservations: z.array(
            z.object({
              kind: z.enum(['own', 'other']),
              startDate: z.string().date(),
              endDate: z.string().date().nullable(),
            })
          ),
          availableRanges: z.array(
            z.object({
              startDate: z.string().date(),
              endDate: z.string().date().nullable(),
            })
          ),
        })
      ),
      costPoolId: z.number().int(),
    })
  ),
  limitations: z
    .object({
      perContractSlotLimit: z.number().int(),
      complimentarySlotsPerContract: z.number().int(),
      canTenantReserveSlotsForDisabled: z.boolean(),
    })
    .nullable(),
  reservationPossibleUntil: z.string().date().nullable(),
})

export type ParkingSpotsResponse = z.infer<typeof ParkingSpotsResponseSchema>

export const CreateParkingSpotReservationRequestSchema = z.object({
  startDate: z.string().date(),
  endDate: z.string().date().nullish(),
  rentableParkingSpotId: z.number(),
})

export type CreateParkingSpotReservationRequest = z.infer<
  typeof CreateParkingSpotReservationRequestSchema
>

/**
 * Pysäköintipaikkavarauksen päättäminen
 */
export const CreateParkingSpotReservationTerminationRequestSchema = z.object({
  endDate: z.string().date(),
  rentableParkingSpotId: z.number(),
})

export type CreateParkingSpotReservationTerminationRequest = z.infer<
  typeof CreateParkingSpotReservationTerminationRequestSchema
>

/**
 * Pysäköintipaikkavarauksen peruminen
 */
export const CancelParkingSpotReservationRequestSchema = z.object({
  rentableParkingSpotId: z.number(),
})

export type CancelParkingSpotReservationRequest = z.infer<
  typeof CancelParkingSpotReservationRequestSchema
>

/**
 * Varauksen luontipyyntö
 */
export const CreateReservationRequestSchema = z.object({
  timeSlotIds: z.array(z.number().int()).min(1),
  reservationCalendarId: z.number().int(),
  isRecurring: z.boolean(),
  validUntil: z.string().date().nullish(),
})

export type CreateReservationRequest = z.infer<
  typeof CreateReservationRequestSchema
>

/**
 * Varauksen luonnin vastaus
 */
export const CreateReservationResponseSchema = z.object({
  reservations: z.array(
    z.object({
      id: z.number().int(),
      reservationRecurrenceId: z.number().int().nullable(),
    })
  ),
})

export type CreateReservationResponse = z.infer<
  typeof CreateReservationResponseSchema
>

/**
 * Varauksen peruutuspyyntö
 */
export const CancelReservationRequestSchema = z.object({
  reservationId: z.number().int(),
})

export type CancelReservationRequest = z.infer<
  typeof CancelReservationRequestSchema
>

/**
 * Varauksen peruutuksen vastaus
 */
export const CancelReservationResponseSchema = z.object({
  cancelledReservations: z.array(
    z.object({
      id: z.number().int(),
    })
  ),
})

export type CancelReservationResponse = z.infer<
  typeof CancelReservationResponseSchema
>

/**
 * Toistuvan varauksen peruutuspyyntö
 */
export const CancelRecurringReservationRequestSchema = z.object({
  reservationRecurrenceId: z.number().int(),
})

export type CancelRecurringReservationRequest = z.infer<
  typeof CancelRecurringReservationRequestSchema
>

/**
 * Toistuvan varauksen peruutuksen vastaus
 */
export const CancelRecurringReservationResponseSchema = z.object({
  cancelledRecurrenceId: z.number().int(),
})

export type CancelRecurringReservationResponse = z.infer<
  typeof CancelRecurringReservationResponseSchema
>

/**
 * Toistuvan varauksen päättämispyyntö
 */
export const TerminateRecurringReservationRequestSchema = z.object({
  reservationRecurrenceId: z.number().int(),
  terminateFromDate: z.string().date(),
})

export type TerminateRecurringReservationRequest = z.infer<
  typeof TerminateRecurringReservationRequestSchema
>

/**
 * Toistuvan varauksen päättämisen vastaus
 */
export const TerminateRecurringReservationResponseSchema = z.object({
  terminatedRecurrenceId: z.number().int(),
})

export type TerminateRecurringReservationResponse = z.infer<
  typeof TerminateRecurringReservationResponseSchema
>

/**
 * Varauskalenterin tiedot
 */
export const ReservationCalendarResponseSchema = z.object({
  id: z.number().int(),
  realPropertyHierarchyId: z.number().int(),
  name: z.string(),
  reservableCategory: z.string().nullable(),
  reservableTimeStart: z.string(),
  reservableTimeEnd: z.string(),
  weeklySlotDuration: z.string(),
  weeklySlotsPerDay: z.number().int(),
  reservableWeekdays: z.array(z.number().int()),
  maxReservedSlotsPerWeek: z.number().int().nullable(),
  maxReservationsPerSlot: z.number().int(),
  allowSingleReservations: z.boolean(),
  allowRecurringReservations: z.boolean(),
  currentReservationCount: z.number().int(),
})

export type ReservationCalendarResponse = z.infer<
  typeof ReservationCalendarResponseSchema
>

/**
 * Aikavälin tiedot
 */
export const TimeSlotResponseSchema = z.object({
  id: z.number().int(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  duration: z.string(),
  status: z.enum(['free', 'own', 'own_recurring', 'reserved']),
  reservations: z.array(
    z.object({
      id: z.number().int(),
      reservationRecurrenceId: z.number().int().nullable(),
    })
  ),
})

export type TimeSlotResponse = z.infer<typeof TimeSlotResponseSchema>

export const CreateWorkOrderRequestBodySchema = z.object({
  locationId: z.number(),
  title: z.string(),
  description: z.string(),
  fileUuids: z.array(z.string()),
  earliestStartDate: z.string().datetime({ offset: true }),
  latestStartDate: z.string().datetime({ offset: true }),
  masterKeyAccessAllowed: z.boolean(),
  hasPetsInApartment: z.boolean(),
})

export type CreateWorkOrderRequestBody = z.infer<
  typeof CreateWorkOrderRequestBodySchema
>

export const ElectronicInvoiceAddressSchema = z.object({
  businessIdentityCode: z.string().nullish(),
  companyName: z.string().nullish(),
  id: z.number(),
  identifier: z.string(),
  intermediator: z.string(),
})

export type ElectronicInvoiceAddress = z.infer<
  typeof ElectronicInvoiceAddressSchema
>

export const InvoiceAddressResponseSchema = z.object({
  contractAddress: PostalAddressSchema.optional(),
  electronicInvoiceAddress: ElectronicInvoiceAddressSchema.nullish(),
  postalInvoiceAddress: PostalAddressSchema.optional(),
  selectedInvoiceType: z.enum([
    'contract_address',
    'corporate_electronic',
    'postal_invoice_address',
  ]),
  selectedPostalAddressType: z.enum([
    'contract_address',
    'corporate_electronic',
    'postal_invoice_address',
  ]),
})

export type InvoiceAddressResponse = z.infer<
  typeof InvoiceAddressResponseSchema
>

/**
 * Moduulien ja asetusten konfiguraatiotiedot
 */
export const ModuleConfigResponseBodySchema = z.array(
  z.object({
    name: z.string(),
    isEnabled: z.boolean(),
    metadata: z.record(z.unknown()).nullish(),
  })
)

export type ModuleConfigResponseBody = z.infer<
  typeof ModuleConfigResponseBodySchema
>

/**
 * Sopimuksen vuokrattavien tilojen paluusanoma
 */
export const DayReservableAreasResponseSchema = z.object({
  dayReservableAreas: z.array(
    z.object({
      rentableHierarchyId: z.number().int(),
      realPropertyHierarchyId: z.number().int(),
      additionalDetails: z.string().nullable(),
      locationDetails: z.string().nullable(),
      buildingLocationDetails: z.string().optional(),
      buildingPartLocationDetails: z.string(),
      buildingPartAddress: z.object({
        streetName: z.string(),
        streetNumber: z.string(),
        city: z.string(),
      }),
      prices: z.array(
        z.object({
          price: z.number(),
          validFrom: z.string().date(),
          validTo: z.string().date(),
        })
      ),
      reservations: z.array(
        z.object({
          kind: z.enum(['own', 'other']),
          startDate: z.string().date(),
          endDate: z.string().date().nullable(),
        })
      ),
      availableRanges: z.array(
        z.object({
          startDate: z.string().date(),
          endDate: z.string().date().nullable(),
        })
      ),
    })
  ),
})

export type DayReservableAreasResponse = z.infer<
  typeof DayReservableAreasResponseSchema
>

/**
 * Vuokrattavan tilan varauksen luontipyyntö
 */
export const CreateDayReservableAreaReservationRequestSchema = z.object({
  starts: z.enum(['today', 'next_month']),
  rentableHierarchyId: z.number().int(),
})

export type CreateDayReservableAreaReservationRequest = z.infer<
  typeof CreateDayReservableAreaReservationRequestSchema
>

/**
 * Vuokrattavan tilan varauksen päättämispyyntö
 */
export const TerminateDayReservableAreaReservationRequestSchema = z.object({
  rentableHierarchyId: z.number().int(),
  endDate: z.string().date(),
})

export type TerminateDayReservableAreaReservationRequest = z.infer<
  typeof TerminateDayReservableAreaReservationRequestSchema
>

/**
 * resident/operation-confirmation/OperationType
 *
 * Operaation tyyppi
 */
export const OperationTypeSchema = z.enum([
  'resident_app_accept_contract_modification',
  'resident_app_accept_contract_termination',
  'resident_app_confirm_contract_modification',
  'resident_app_confirm_contract_termination',
  'resident_app_reject_contract_modification',
  'resident_app_reject_contract_termination',
])

export type OperationType = z.infer<typeof OperationTypeSchema>

/**
 * resident/operation-confirmation/FindOperationStateResponse
 *
 * Operaation vahvistustilan haku
 */
export const ConfirmOperationStateResponseBodySchema = z.object({
  state: z.enum(['confirmed', 'pending', 'rejected']),
  operationType: OperationTypeSchema,
})

export type ConfirmOperationStateResponseBody = z.infer<
  typeof ConfirmOperationStateResponseBodySchema
>

/**
 * resident/operation-confirmation/PendingOperationsResponse
 *
 * Keskeneräiset operaatiot
 */
export const PendingOperationsResponseBodySchema = z.array(
  z.object({
    nonce: z.string().uuid(),
    operationType: OperationTypeSchema,
    contractUuid: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
  })
)

export type PendingOperationsResponseBody = z.infer<
  typeof PendingOperationsResponseBodySchema
>

/**
 * resident/operation-confirmation/RejectPendingOperationResponse
 *
 * Keskeneräisen operaation hylkäyksen vastaus
 */
export const RejectPendingOperationResponseBodySchema = z.object({
  success: z.boolean(),
})

export type RejectPendingOperationResponseBody = z.infer<
  typeof RejectPendingOperationResponseBodySchema
>

/**
 * Vuokrattavan tilan varauksen perumispyyntö
 */
export const CancelDayReservableAreaReservationRequestSchema = z.object({
  rentableHierarchyId: z.number().int(),
})

export type CancelDayReservableAreaReservationRequest = z.infer<
  typeof CancelDayReservableAreaReservationRequestSchema
>

/**
 * Varattavien tilojen paluusanoma
 */
export const SharedAreasResponseSchema = z.object({
  sharedAreas: z.array(
    z.object({
      id: z.number().int(),
      realPropertyHierarchyId: z.number().int(),
      type: z.enum(['gym']),
      buildingLocationDetails: z.string().optional(),
      buildingPartLocationDetails: z.string(),
      locationDetails: z.string(),
      additionalDetails: z.string().nullable(),
      totalSeats: z.number().int(),
      maxOwnSeats: z.number().int(),
      ownSeats: z.number().int(),
      availableSeats: z.number().int(),
      buildingPartAddress: z.object({
        streetName: z.string(),
        streetNumber: z.string(),
        city: z.string(),
      }),
      monthlyPrices: z.array(
        z.object({
          priceIncludingVat: z.number(),
          vatPercentage: z.number(),
          validFrom: z.string().date(),
          validTo: z.string().date(),
        })
      ),
      canReserve: z.boolean(),
    })
  ),
})

export type SharedAreasResponse = z.infer<typeof SharedAreasResponseSchema>

/**
 * Sopimuksen käyttöoikeuksien paluusanoma
 */
export const RightsOfAccessResponseSchema = z.object({
  rightsOfAccess: z.array(
    z.object({
      id: z.number().int(),
      realPropertyHierarchyId: z.number().int(),
      startDate: z.string().date(),
      endDate: z.string().date().nullable(),
      type: z.enum(['gym']),
      buildingLocationDetails: z.string(),
      buildingPartLocationDetails: z.string(),
      locationDetails: z.string(),
      additionalDetails: z.string().nullable(),
      buildingPartAddress: z.object({
        streetName: z.string(),
        streetNumber: z.string(),
        city: z.string(),
      }),
    })
  ),
})

export type RightsOfAccessResponse = z.infer<
  typeof RightsOfAccessResponseSchema
>

/**
 * Käyttöoikeuden luontipyyntö
 */
export const CreateRightOfAccessRequestSchema = z.object({
  starts: z.enum(['today', 'next_month']),
  sharedAreaId: z.number().int(),
})

export type CreateRightOfAccessRequest = z.infer<
  typeof CreateRightOfAccessRequestSchema
>

/**
 * Sopimuksen tunniste
 */
export const ContractUuidSchema = z.string().uuid()

export type ContractUuid = z.infer<typeof ContractUuidSchema>

/**
 * Sopimusmuutoksen tunniste
 */
export const ContractModificationRequestUuidSchema = z.string().uuid()

export type ContractModificationRequestUuid = z.infer<
  typeof ContractModificationRequestUuidSchema
>

/**
 * Default Response
 */
export const zGetResidentRestUserProfileResponse = z.object({
  statusCode: z.number().int().optional(),
  data: UserDetailsSchema.optional(),
})

/**
 * Default Response
 */
export const zPatchResidentRestUserProfileResponse = z.object({
  statusCode: z.number().int().optional(),
  data: z
    .object({
      success: z.boolean(),
    })
    .optional(),
})

/**
 * Ilmoitukset käyttäjälle
 */
export const zGetResidentRestUserNotificationsResponse = z.object({
  statusCode: z.number().int().optional(),
  data: z.array(UserNotificationSchema).optional(),
})

/**
 * Notifikaatio suljettu
 */
export const zPostResidentRestUserNotificationsByIdCloseResponse = z.object({
  id: z.number().int().optional(),
  closedAt: z.string().datetime({ offset: true }).optional(),
})

/**
 * Default Response
 */
export const zGetResidentRestUserPermissionsResponse = z.object({
  statusCode: z.number().int().optional(),
  data: UserPermissionsSchema.optional(),
})

/**
 * Aktiiviset sopimukset
 */
export const zGetResidentRestContractsResponse = z.object({
  statusCode: z.number().int().optional(),
  data: z.array(ContractSchema).optional(),
})

/**
 * Sopimus
 */
export const zGetResidentRestContractsByContractUuidResponse = z.object({
  statusCode: z.number().int().optional(),
  data: ContractSchema.optional(),
})

/**
 * Sopimukset asukkaat
 */
export const zGetResidentRestContractsByContractUuidTenantsResponse = z.object({
  statusCode: z.number().int().optional(),
  data: z.array(ContractTenantSchema).optional(),
})

/**
 * Irtisanomisilmoituksen tiedot
 */
export const zGetResidentRestContractsByContractUuidTerminationResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: ContractTerminationSchema.optional(),
  })

/**
 * Irtisanomisilmoitus luotu
 */
export const zTerminateContractResponse = z.object({
  statusCode: z.number().int().optional(),
  data: ContractTerminationResponseBodySchema.optional(),
})

/**
 * Sopimusmuutoksen data
 */
export const zGetResidentRestContractsByContractUuidModificationResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: ContractModificationResponseWithEventsBodySchema.optional(),
  })

/**
 * Sopimusmuutoksen data
 */
export const zModifyContractResponse = z.object({
  statusCode: z.number().int().optional(),
  data: ContractModificationResponseBodySchema.optional(),
})

/**
 * Contract modification initialized successfully.
 */
export const zPostResidentRestContractsByContractUuidConfirmOperationInitContractModificationCreateResponse =
  z.object({
    nonce: z.string().uuid().optional(),
  })

/**
 * Sopimukset olemassaolevat maksunsaajat
 */
export const zGetResidentRestContractsByContractUuidBeneficiariesResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.array(BeneficiarySchema).optional(),
  })

/**
 * Default Response
 */
export const zPostResidentRestContractsByContractUuidConfirmOperationInitContractTerminationCreateResponse =
  z.object({
    data: ConfirmOperationInitResponseBodySchema,
  })

/**
 * Default Response
 */
export const zPostResidentRestContractsByContractUuidConfirmOperationInitContractTerminationApproveResponse =
  z.object({
    data: ConfirmOperationInitResponseBodySchema,
  })

/**
 * Default Response
 */
export const zPostResidentRestContractsByContractUuidConfirmOperationInitContractModificationApproveResponse =
  z.object({
    data: ConfirmOperationInitResponseBodySchema,
  })

/**
 * Default Response
 */
export const zPostResidentRestConfirmOperationResponse =
  ConfirmOperationExecuteResponseBodySchema

/**
 * Default Response
 */
export const zGetResidentRestConfirmOperationByNonceStateResponse = z.object({
  statusCode: z.number().int().optional(),
  data: ConfirmOperationStateResponseBodySchema.optional(),
})

/**
 * Default Response
 */
export const zGetResidentRestPendingOperationsResponse = z.object({
  statusCode: z.number().int().optional(),
  data: PendingOperationsResponseBodySchema.optional(),
})

/**
 * Default Response
 */
export const zPostResidentRestPendingOperationsByNonceRejectResponse = z.object(
  {
    statusCode: z.number().int().optional(),
    data: RejectPendingOperationResponseBodySchema.optional(),
  }
)

/**
 * Default Response
 */
export const zGetResidentRestContractsByContractUuidBulletinsResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: PaginatedBulletinsResponseSchema.optional(),
  })

/**
 * Default Response
 */
export const zGetResidentRestContractsByContractUuidBulletinsByBulletinUuidResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: BulletinResponseBodySchema.optional(),
  })

/**
 * Sopimuksen dokumentit
 */
export const zGetResidentRestContractsByContractUuidBulletinsByBulletinUuidAttachmentsByAttachmentUuidResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z
      .object({
        url: z.string().optional(),
      })
      .optional(),
  })

/**
 * Default Response
 */
export const zPostResidentRestFileInitResponse = z.object({
  statusCode: z.number().int().optional(),
  data: FileInitResponseBodySchema.optional(),
})

/**
 * Default Response
 */
export const zPostResidentRestFileCompleteResponse = z.object({
  statusCode: z.number().int().optional(),
  data: FileCompleteResponseBodySchema.optional(),
})

/**
 * Sopimuksen laskuerittelyt kuukausittain
 */
export const zGetResidentRestContractsByContractUuidPaymentsByMonthResponse =
  z.object({
    statusCode: z.number().int(),
    data: z.array(PaymentsByMonthSchema),
  })

/**
 * Sopimuksen maksutapahtumat
 */
export const zGetResidentRestContractsByContractUuidRemittancesResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.array(RemittanceSchema).optional(),
  })

/**
 * Sopimuksen maksukategoriat
 */
export const zGetResidentRestContractsByContractUuidPaymentCategoriesResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.array(PaymentCategorySchema).optional(),
  })

/**
 * Sopimuksen kuukausittaisten maksutavoitteiden summat.
 */
export const zGetResidentRestContractsByContractUuidPaymentsTargetsCurrentResponse =
  CurrentPaymentTargetsResponseSchema

/**
 * Sopimuksen velkaerittely
 */
export const zGetResidentRestContractsByContractUuidDebtResponse = z.object({
  data: ContractDebtSchema,
  statusCode: z.number().int(),
})

/**
 * Default Response
 */
export const zGetResidentRestDefectNoticeClassesResponse = z.object({
  statusCode: z.number().int().optional(),
  data: DefectNoticeClassesSchema.optional(),
})

/**
 * Default Response
 */
export const zPostResidentRestDefectNoticeResponse = z.object({
  statusCode: z.number().int().optional(),
  data: CreateDefectNoticeResponseBodySchema.optional(),
})

/**
 * Default Response
 */
export const zPostResidentRestQuestionResponse = z.object({
  statusCode: z.number().int().optional(),
  data: CreateQuestionResponseBodySchema.optional(),
})

/**
 * Default Response
 */
export const zPostResidentRestFeedbackResponse = z.object({
  statusCode: z.number().int().optional(),
  data: CreateFeedbackResponseBodySchema.optional(),
})

/**
 * Sopimuksen kiinteisön yhteystiedot
 */
export const zGetResidentRestContractsByContractUuidContactsResponse = z.object(
  {
    statusCode: z.number().int().optional(),
    data: z.array(ContractContactSchema).optional(),
  }
)

/**
 * Sopimuksen dokumentit
 */
export const zGetResidentRestContractsByContractUuidDocumentsResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.array(DocumentsResponseSchema).optional(),
  })

/**
 * Sopimuksen yksittäinen dokumentti
 */
export const zGetResidentRestContractsByContractUuidDocumentsByDocumentUuidResponse =
  z.object({
    uuid: z.string().uuid().optional(),
    url: z.string().optional(),
  })

/**
 * Sopimusdokumentin saatavuus
 */
export const zGetResidentRestContractsByContractUuidContractDocumentResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: ContractDocumentResponseSchema.optional(),
  })

/**
 * Sopimusdokumentin sisältö
 */
export const zGetResidentRestContractsByContractUuidContractDocumentContentResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: ContractDocumentContentResponseSchema.optional(),
  })

/**
 * Sopimuksen vikailmoitukset
 */
export const zGetResidentRestContractsByContractUuidDefectNoticesResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.array(DefectNoticeListItemSchema).optional(),
  })

/**
 * Vikailmoituksen yksityiskohdat
 */
export const zGetResidentRestContractsByContractUuidDefectNoticesByDefectNoticeIdResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: DefectNoticeSchema.optional(),
  })

/**
 * Vikailmoituksen tiedot päivitetty onnistuneesti
 */
export const zPatchResidentRestContractsByContractUuidDefectNoticesByDefectNoticeIdResponse =
  z.void()

/**
 * Käyttäjän sopimusdatatarkistus onnistui.
 */
export const zGetResidentUnboundRestUserBindingStatusResponse = z.object({
  statusCode: z.number().int(),
  data: z.object({
    bound: z.boolean(),
    provider: z.enum(['ftn', 'passport']).nullable(),
  }),
})

/**
 * Sopimuksen kiinteistön tiedot
 */
export const zGetResidentRestContractsByContractUuidBuildingResponse = z.object(
  {
    statusCode: z.number().int(),
    data: BuildingSchema,
  }
)

/**
 * Sopimuksen pysäköintipaikkojen tiedot
 */
export const zGetResidentRestContractsByContractUuidParkingSpotsResponse =
  z.object({
    data: ParkingSpotsResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Pysäköintipaikkavaraus luotu
 */
export const zPostResidentRestContractsByContractUuidParkingSpotsReservationsResponse =
  z.void()

/**
 * Pysäköintipaikkavaraus päätetty
 */
export const zPostResidentRestContractsByContractUuidParkingSpotsReservationsTerminateResponse =
  z.void()

/**
 * Pysäköintipaikkavaraus peruttu
 */
export const zPostResidentRestContractsByContractUuidParkingSpotsReservationsCancelResponse =
  z.void()

/**
 * Sopimuksen varauskalenterit
 */
export const zGetResidentRestContractsByContractUuidReservationCalendarsResponse =
  z.object({
    data: z.array(ReservationCalendarResponseSchema),
    statusCode: z.number().int(),
  })

/**
 * Varauskalenterin aikavälit
 */
export const zGetResidentRestContractsByContractUuidReservationCalendarsByCalendarIdTimeSlotsResponse =
  z.object({
    data: z.object({
      timeSlots: z.array(TimeSlotResponseSchema),
      calendarMetadata: z.object({
        id: z.number().int(),
        name: z.string(),
        equipmentCategory: z.string().nullable(),
        equipmentTypeName: z.string().nullable(),
        reservableTimeStart: z.string(),
        reservableTimeEnd: z.string(),
        weeklySlotDuration: z.string(),
        weeklySlotsPerDay: z.number().int().nullable(),
        reservableWeekdays: z.array(z.number().int()),
        maxReservationsPerSlot: z.number().int(),
      }),
      pricesByDate: z.record(
        z.object({
          monthlyPrice: z.string().nullable(),
          upcomingRecurringUnitPrice: z.string().nullable(),
          upcomingRecurringUnitPriceValidFrom: z.string().nullable(),
          oncePrice: z.string().nullable(),
        })
      ),
    }),
    statusCode: z.number().int(),
  })

/**
 * Varauskäytännöt ja ehdot
 */
export const zGetResidentRestContractsByContractUuidReservationRulesAndTermsResponse =
  z.object({
    data: z.object({
      files: z.array(
        z.object({
          url: z.string(),
          displayFileName: z.string(),
          attachmentType: z
            .union([
              z.literal('sauna_rules'),
              z.literal('sauna_terms'),
              z.literal('laundry_rules'),
              z.literal('laundry_terms'),
              z.literal('gym_rules'),
              z.literal('gym_terms'),
              z.literal('common_area_rules'),
              z.literal('common_area_terms'),
              z.literal('storage_rules'),
              z.literal('storage_terms'),
              z.literal('parking_spot_rules'),
              z.literal('parking_spot_terms'),
              z.literal('floor_map'),
              z.literal('space_image'),
            ])
            .nullable(),
        })
      ),
      externalLinks: z.array(
        z.object({
          url: z.string(),
          title: z.string(),
          attachmentType: z
            .union([
              z.literal('sauna_rules'),
              z.literal('sauna_terms'),
              z.literal('laundry_rules'),
              z.literal('laundry_terms'),
              z.literal('gym_rules'),
              z.literal('gym_terms'),
              z.literal('common_area_rules'),
              z.literal('common_area_terms'),
              z.literal('storage_rules'),
              z.literal('storage_terms'),
              z.literal('parking_spot_rules'),
              z.literal('parking_spot_terms'),
              z.literal('floor_map'),
              z.literal('space_image'),
            ])
            .nullable(),
        })
      ),
    }),
    statusCode: z.number().int(),
  })

/**
 * Varaus luotu onnistuneesti
 */
export const zPostResidentRestContractsByContractUuidReservationsResponse =
  z.object({
    data: CreateReservationResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Varaus peruutettu onnistuneesti
 */
export const zPostResidentRestContractsByContractUuidReservationsCancelResponse =
  z.object({
    data: CancelReservationResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Toistuva varaus peruutettu onnistuneesti
 */
export const zPostResidentRestContractsByContractUuidReservationsCancelRecurringResponse =
  z.object({
    data: CancelRecurringReservationResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Toistuva varaus päätetty onnistuneesti
 */
export const zPostResidentRestContractsByContractUuidReservationsTerminateResponse =
  z.object({
    data: TerminateRecurringReservationResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Käyttäjän pysäköintipaikkajonon tiedot
 */
export const zGetResidentRestContractsByContractUuidParkingSpotsQueueResponse =
  z.object({
    statusCode: z.number().int(),
    data: z.array(
      z.object({
        id: z.number().int(),
        equipmentWishes: z.array(ParkingSpotEquipmentEnumSchema),
        kind: z.enum(['own', 'other']),
        queuePosition: z.number().int(),
        additionalInformation: z.string().optional(),
        costPoolId: z.number().int(),
      })
    ),
  })

/**
 * Liittyminen jonoon onnistui
 */
export const zPostResidentRestContractsByContractUuidParkingSpotsQueueResponse =
  z.void()

/**
 * Poistuminen jonosta onnistui
 */
export const zDeleteResidentRestContractsByContractUuidParkingSpotsByCostPoolIdQueueByQueueIdResponse =
  z.void()

/**
 * Työtilaus luotu onnistuneesti
 */
export const zPostResidentRestContractsByContractUuidWorkOrdersResponse =
  z.object({
    id: z.number().optional(),
  })

/**
 * Sisäänmuuttotarkastus luotu
 */
export const zPostResidentRestContractsByContractUuidTenantInspectionsResponse =
  z.object({
    statusCode: z.number().int().optional(),
    data: z.object({
      tenantInspectionUuid: z.string().uuid(),
    }),
  })

/**
 * Sopimuksen asunnon tiedot
 */
export const zGetResidentRestContractsByContractUuidApartmentResponse =
  z.object({
    statusCode: z.number().int(),
    data: ApartmentSchema,
  })

/**
 * Sopimuksen vakuustiedot
 */
export const zGetResidentRestContractsByContractUuidSecurityDepositsResponse =
  z.object({
    data: ContractSecurityDepositResponseSchema.optional(),
    statusCode: z.number().int().optional(),
  })

/**
 * Sopimuksen laskutustiedot
 */
export const zGetResidentRestContractsByContractUuidInvoiceAddressResponse =
  z.object({
    data: InvoiceAddressResponseSchema.optional(),
    statusCode: z.number().int().optional(),
  })

/**
 * Default Response
 */
export const zGetResidentRestModuleConfigByContractUuidResponse = z.object({
  statusCode: z.number().int().optional(),
  data: ModuleConfigResponseBodySchema.optional(),
})

/**
 * Vuokrattavien tilojen tiedot
 */
export const zGetResidentRestContractsByContractUuidDayReservableAreasResponse =
  z.object({
    data: DayReservableAreasResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Vuokrattavan tilan varaus luotu
 */
export const zPostResidentRestContractsByContractUuidDayReservableAreasReservationsResponse =
  z.void()

/**
 * Vuokrattavan tilan varaus päätetty
 */
export const zPostResidentRestContractsByContractUuidDayReservableAreasReservationsTerminateResponse =
  z.void()

/**
 * Vuokrattavan tilan varaus peruttu
 */
export const zPostResidentRestContractsByContractUuidDayReservableAreasReservationsCancelResponse =
  z.void()

/**
 * Varattavien tilojen tiedot
 */
export const zGetResidentRestContractsByContractUuidSharedAreasResponse =
  z.object({
    data: SharedAreasResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Sopimuksen käyttöoikeudet
 */
export const zGetResidentRestContractsByContractUuidRightsOfAccessResponse =
  z.object({
    data: RightsOfAccessResponseSchema,
    statusCode: z.number().int(),
  })

/**
 * Käyttöoikeus luotu
 */
export const zPostResidentRestContractsByContractUuidRightsOfAccessResponse =
  z.void()

/**
 * Käyttöoikeus päätetty
 */
export const zPostResidentRestContractsByContractUuidRightsOfAccessByRightOfAccessIdTerminateResponse =
  z.void()
